import { createElement } from 'react';
import { getChartTitle } from '@/shared/copy/charts';
import { getStatusCopy } from '@/shared/copy/status';
import {
  isMetricThresholdDisabled,
  metricThresholdsSuites,
  type MetricThreshold,
} from '@/shared/utils/metricsThresholds';
import { typographyTokens } from '@/theme/tokens';
import { Box, Tooltip } from '@/ui/primitives';
import {
  getStatusToneTextColor,
  normaliseSummaryChipStatus,
  type StatusChipStatus,
} from '@/ui/primitives';
import { formatDuration } from '../../formatters/formatDuration';
import { formatFrequency } from '../../formatters/formatFrequency';
import { formatPercent } from '../../formatters/formatPercent';
import { getDeltaTextColor } from './deltaColors';
import { MetricChipCell } from './tableCells';
import type { TestSummaryItem } from './tableTypes';
import type { BoxProps } from '@/ui/primitives';
import type {
  ReleaseFeatureSummaryDto,
  ReleaseSuiteSummaryDto,
  ReleaseTestSummaryDto,
  SuiteRunSummaryDto,
  TestRunSummaryDto,
} from '@flowqa/shared';
import type { Theme } from '@mui/material/styles';
import type {
  GridAlignment,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import type { ReactNode } from 'react';

export type MetricThresholds = MetricThreshold;
type ThresholdChipKey = 'green' | 'yellow' | 'red';

export const getThresholdChipKey = (
  value: number | null | undefined,
  thresholds: MetricThresholds,
): ThresholdChipKey | null => {
  if (value === null || value === undefined) return null;
  if (isMetricThresholdDisabled(thresholds)) return null;
  if (thresholds.direction === 'higherBetter') {
    if (value >= thresholds.target) return 'green';
    if (value >= thresholds.warn) return 'yellow';
    return 'red';
  }
  if (value <= thresholds.target) return 'green';
  if (value <= thresholds.warn) return 'yellow';
  return 'red';
};

export type MetricColumnKey = 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';

export const STANDARD_METRIC_COLUMN_FLEX = 0.68;

export const metricColumnOrder: MetricColumnKey[] = [
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
];

export type MetricColumnFields = {
  passRate: string;
  frequency: string;
  flakeScore: string;
  avgDuration: string;
};

export type TableMetricColumnsConfig<T extends GridValidRowModel> = {
  fields: Partial<MetricColumnFields>;
  flex?: Partial<Record<MetricColumnKey, number>>;
  deltaFlex?: Partial<Record<MetricColumnKey, number>>;
  deltaFields?: Partial<Record<MetricColumnKey, string>>;
  headers?: Partial<Record<MetricColumnKey, string>>;
  thresholds?: Partial<Record<MetricColumnKey, MetricThresholds>>;
  resolveThresholds?: (row: T) => Partial<Record<MetricColumnKey, MetricThresholds>> | undefined;
  enabledKeys?: MetricColumnKey[];
  includeDeltas?: boolean | ((key: MetricColumnKey) => boolean);
  formatters?: Partial<Record<MetricColumnKey, (value: number) => string>>;
  deltaFormatters?: Partial<Record<MetricColumnKey, (value: number) => string>>;
  thresholdValueTransforms?: Partial<Record<MetricColumnKey, (value: number) => number>>;
  deltaRenderers?: Partial<
    Record<MetricColumnKey, (value: number | null | undefined) => ReactNode>
  >;
  align?: GridAlignment;
  headerAlign?: GridAlignment;
};

const DEFAULT_METRIC_HEADERS: Record<MetricColumnKey, string> = {
  passRate: getChartTitle('passRate'),
  frequency: getChartTitle('frequency'),
  flakeScore: getChartTitle('flakeScore'),
  avgDuration: getChartTitle('avgDuration'),
};

const DEFAULT_METRIC_FLEX: Record<MetricColumnKey, number> = {
  passRate: 1,
  frequency: 1,
  flakeScore: 1,
  avgDuration: 1,
};

const DEFAULT_METRIC_DELTA_FLEX: Record<MetricColumnKey, number> = {
  passRate: 0.8,
  frequency: 0.8,
  flakeScore: 0.8,
  avgDuration: 0.8,
};

const getNumberFieldValueByKey = (
  row: GridValidRowModel | null | undefined,
  field: string,
): number | null | undefined => {
  if (!row) return null;
  const value = field
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
      row,
    );
  if (typeof value === 'number') return value;
  if (value === null) return null;
  if (value === undefined) return undefined;
  return undefined;
};

const isPromiseLike = <T>(value: unknown): value is PromiseLike<T> =>
  typeof value === 'object' &&
  value !== null &&
  'then' in value &&
  typeof (value as { then?: unknown }).then === 'function';

const METRIC_ALIGNMENT: Pick<GridColDef, 'align' | 'headerAlign'> = {
  align: 'center',
  headerAlign: 'center',
};

const METRIC_TEXT_KEYS: MetricColumnKey[] = ['passRate', 'frequency', 'avgDuration', 'flakeScore'];
const NO_DATA_LABEL = getStatusCopy('noData');
const NUMERIC_TEXT_SX = {};
const DELTA_TEXT_SX = {
  display: 'block',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

export const buildMetricColumns = <T extends GridValidRowModel>(
  options: TableMetricColumnsConfig<T>,
): Array<GridColDef<T>> => {
  const headers = { ...DEFAULT_METRIC_HEADERS, ...options.headers };
  const flex = { ...DEFAULT_METRIC_FLEX, ...options.flex };
  const deltaFlex = { ...DEFAULT_METRIC_DELTA_FLEX, ...options.deltaFlex };
  const enabledKeys = options.enabledKeys ?? metricColumnOrder;
  const includeDeltas = options.includeDeltas ?? true;
  const includeDeltaForKey = (key: MetricColumnKey): boolean =>
    typeof includeDeltas === 'function' ? includeDeltas(key) : includeDeltas;
  const formatters = options.formatters ?? {};
  const deltaFormatters = options.deltaFormatters ?? {};
  const thresholdValueTransforms = options.thresholdValueTransforms ?? {};
  const deltaRenderers = options.deltaRenderers ?? {};
  const { fields } = options;
  const deltaFields = options.deltaFields ?? {};
  const metricAlign = options.align ?? METRIC_ALIGNMENT.align;
  const metricHeaderAlign = options.headerAlign ?? METRIC_ALIGNMENT.headerAlign;
  const resolveThresholds = options.resolveThresholds;
  const thresholds: Record<MetricColumnKey, MetricThresholds> = {
    passRate: metricThresholdsSuites.passRate,
    frequency: metricThresholdsSuites.frequency,
    flakeScore: metricThresholdsSuites.flakeScore,
    avgDuration: metricThresholdsSuites.avgDuration,
    ...options.thresholds,
  };

  type MetricColumnConfig = {
    key: MetricColumnKey;
    field?: string;
    header: string;
    flex: number;
    formatValue: (value: number) => string;
    thresholdValue?: (value: number) => number;
  };

  type MetricDeltaConfig = {
    key: MetricColumnKey;
    field: string;
    header: string;
    description: string;
    flex: number;
    formatValue: (value: number) => string;
    direction: 'higherBetter' | 'lowerBetter';
  };

  const createMetricColumn = (config: MetricColumnConfig & { field: string }): GridColDef<T> => {
    const column: GridColDef<T> = {
      field: config.field,
      headerName: config.header,
      flex: config.flex,
      sortable: false,
      filterable: false,
      valueGetter: (_value, row) => getNumberFieldValueByKey(row, config.field),
    };
    column.renderCell = (params: GridRenderCellParams<T, number | null | undefined>) => {
      const value = getNumberFieldValueByKey(params.row, config.field);
      const resolvedThresholds = resolveThresholds?.(params.row);
      const thresholdConfig = resolvedThresholds?.[config.key] ?? thresholds[config.key];
      const thresholdValue =
        value === null || value === undefined
          ? null
          : config.thresholdValue
            ? config.thresholdValue(value)
            : value;
      const accentColor = getThresholdChipKey(thresholdValue, thresholdConfig);
      const displayValue = value === null || value === undefined ? '-' : config.formatValue(value);
      if (METRIC_TEXT_KEYS.includes(config.key)) {
        const tone = mapChipKeyToStatusTone(accentColor);
        const color =
          value === null || value === undefined
            ? 'text.secondary'
            : accentColor === null
              ? 'text.primary'
              : (muiTheme: Theme): string => getStatusToneTextColor(muiTheme, tone);
        return createElement(
          Box,
          {
            component: 'span',
            sx: {
              color,
              fontSize: (muiTheme: Theme): string | number =>
                muiTheme.typography.body.fontSize ?? 'inherit',
              fontWeight: typographyTokens.body.fontWeight,
              ...NUMERIC_TEXT_SX,
            },
          } as BoxProps,
          displayValue,
        );
      }
      return createElement(MetricChipCell, { params, accentColor, displayValue });
    };
    Object.assign(column, { align: metricAlign, headerAlign: metricHeaderAlign });
    return column;
  };

  const formatDeltaValue = (
    value: number | null | undefined,
    formatValue: (absoluteValue: number) => string,
    direction: MetricDeltaConfig['direction'],
  ): { label: string; isGood: boolean } | null => {
    if (value === null || value === undefined || value === 0) return null;
    const absolute = Math.abs(value);
    const formatted = formatValue(absolute);
    const sign = value > 0 ? '+' : '-';
    const isPositive = value > 0;
    const isGood = direction === 'higherBetter' ? isPositive : !isPositive;
    return { label: `${sign}${formatted}`, isGood };
  };

  const createMetricDeltaColumn = (config: MetricDeltaConfig): GridColDef<T> => {
    const column: GridColDef<T> = {
      field: config.field,
      headerName: config.header,
      description: config.description,
      flex: config.flex,
      sortable: false,
      filterable: false,
      renderHeader: () =>
        createElement(Tooltip, {
          title: config.description,
          placement: 'top',
          arrow: true,
          slotProps: {
            tooltip: { sx: { fontSize: '15px', lineHeight: 1.4 } },
          },
          children: createElement(
            Box,
            {
              component: 'span',
              sx: { cursor: 'help' },
            } as BoxProps,
            config.header,
          ),
        }),
      valueGetter: (_value, row) => getNumberFieldValueByKey(row, config.field),
      renderCell: (params) => {
        const value = getNumberFieldValueByKey(params.row, config.field);
        const result = formatDeltaValue(value, config.formatValue, config.direction);
        const fontSize = typographyTokens.body.fontSize;
        const fontWeight = typographyTokens.body.fontWeight;
        if (!result) {
          return createElement(
            Box,
            {
              component: 'span',
              sx: {
                fontSize,
                fontWeight,
                color: 'text.secondary',
                ...NUMERIC_TEXT_SX,
                ...DELTA_TEXT_SX,
              },
            } as BoxProps,
            NO_DATA_LABEL,
          );
        }
        const color = (muiTheme: Theme): string => getDeltaTextColor(muiTheme, result.isGood);
        return createElement(
          Box,
          {
            component: 'span',
            sx: { color, fontSize, fontWeight, ...NUMERIC_TEXT_SX, ...DELTA_TEXT_SX },
            title: result.label,
          } as BoxProps,
          result.label,
        );
      },
    };
    Object.assign(column, { align: metricAlign, headerAlign: metricHeaderAlign });
    return column;
  };

  const metricConfigs: Record<MetricColumnKey, MetricColumnConfig> = {
    passRate: {
      key: 'passRate',
      field: fields.passRate,
      header: headers.passRate,
      flex: flex.passRate,
      formatValue: formatters.passRate ?? ((value) => `${formatPercent(value)}%`),
      thresholdValue: thresholdValueTransforms.passRate,
    },
    frequency: {
      key: 'frequency',
      field: fields.frequency,
      header: headers.frequency,
      flex: flex.frequency,
      formatValue: formatters.frequency ?? ((value) => formatFrequency(value)),
      thresholdValue: thresholdValueTransforms.frequency,
    },
    flakeScore: {
      key: 'flakeScore',
      field: fields.flakeScore,
      header: headers.flakeScore,
      flex: flex.flakeScore,
      formatValue: formatters.flakeScore ?? ((value) => `${formatPercent(value)}%`),
      thresholdValue: thresholdValueTransforms.flakeScore,
    },
    avgDuration: {
      key: 'avgDuration',
      field: fields.avgDuration,
      header: headers.avgDuration,
      flex: flex.avgDuration,
      formatValue: formatters.avgDuration ?? ((value) => formatDuration(value)),
      thresholdValue: thresholdValueTransforms.avgDuration ?? ((value) => value / 1000),
    },
  };

  const deltaHeader = 'Δ';

  const deltaConfigs: Record<MetricColumnKey, MetricDeltaConfig> = {
    passRate: {
      key: 'passRate',
      field:
        deltaFields.passRate ?? (fields.passRate ? `${fields.passRate}Delta` : 'passRateDelta'),
      header: deltaHeader,
      description: `${headers.passRate} Delta`,
      flex: deltaFlex.passRate,
      formatValue: deltaFormatters.passRate ?? ((value) => `${formatPercent(value)}%`),
      direction: 'higherBetter',
    },
    frequency: {
      key: 'frequency',
      field:
        deltaFields.frequency ?? (fields.frequency ? `${fields.frequency}Delta` : 'frequencyDelta'),
      header: deltaHeader,
      description: `${headers.frequency} Delta`,
      flex: deltaFlex.frequency,
      formatValue: deltaFormatters.frequency ?? ((value) => formatFrequency(value)),
      direction: 'higherBetter',
    },
    flakeScore: {
      key: 'flakeScore',
      field:
        deltaFields.flakeScore ??
        (fields.flakeScore ? `${fields.flakeScore}Delta` : 'flakeScoreDelta'),
      header: deltaHeader,
      description: `${headers.flakeScore} Delta`,
      flex: deltaFlex.flakeScore,
      formatValue: deltaFormatters.flakeScore ?? ((value) => `${formatPercent(value)}%`),
      direction: 'lowerBetter',
    },
    avgDuration: {
      key: 'avgDuration',
      field:
        deltaFields.avgDuration ??
        (fields.avgDuration ? `${fields.avgDuration}Delta` : 'avgDurationDelta'),
      header: deltaHeader,
      description: `${headers.avgDuration} Delta`,
      flex: deltaFlex.avgDuration,
      formatValue: deltaFormatters.avgDuration ?? ((value) => formatDuration(value)),
      direction: 'lowerBetter',
    },
  };

  return enabledKeys.flatMap((key) => {
    const metricConfig = metricConfigs[key];
    if (!metricConfig.field) return [];
    const columns: Array<GridColDef<T>> = [
      createMetricColumn({ ...metricConfig, field: metricConfig.field }),
    ];
    if (!includeDeltaForKey(key)) {
      return columns;
    }
    const customDeltaRenderer = deltaRenderers[key];
    if (customDeltaRenderer) {
      const deltaColumn: GridColDef<T> = {
        field: deltaConfigs[key].field,
        headerName: deltaConfigs[key].header,
        description: deltaConfigs[key].description,
        flex: deltaConfigs[key].flex,
        sortable: false,
        filterable: false,
        renderHeader: () =>
          createElement(Tooltip, {
            title: deltaConfigs[key].description,
            placement: 'top',
            children: createElement(
              Box,
              {
                component: 'span',
                sx: { cursor: 'help' },
              } as BoxProps,
              deltaConfigs[key].header,
            ),
          }),
        valueGetter: (_value, row) => getNumberFieldValueByKey(row, deltaConfigs[key].field),
        renderCell: (params) => {
          const rendered = customDeltaRenderer(
            getNumberFieldValueByKey(params.row, deltaConfigs[key].field),
          );
          if (isPromiseLike(rendered)) return NO_DATA_LABEL;
          const content = rendered ?? NO_DATA_LABEL;
          const title =
            typeof content === 'string' || typeof content === 'number'
              ? String(content)
              : undefined;
          return createElement(
            Box,
            {
              component: 'span',
              sx: DELTA_TEXT_SX,
              title,
            } as BoxProps,
            content,
          );
        },
      };
      Object.assign(deltaColumn, { align: metricAlign, headerAlign: metricHeaderAlign });
      columns.push(deltaColumn);
      return columns;
    }
    columns.push(createMetricDeltaColumn(deltaConfigs[key]));
    return columns;
  });
};

type TestSummaryNameMode = 'display' | 'full';

const trimToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
};

const buildTestSummaryDisplayLabel = (
  summary: Pick<
    TestRunSummaryDto,
    'displayName' | 'groupPath' | 'testName' | 'suiteDisplayName' | 'suiteName'
  >,
  mode: TestSummaryNameMode,
): string | null => {
  const leafLabel = trimToNull(summary.displayName) ?? trimToNull(summary.testName);
  const topLevelGroup = trimToNull(summary.groupPath?.[0]);
  const nestedGroupValue = summary.groupPath?.slice(1).join(' › ').trim();
  const nestedGroupLabel =
    nestedGroupValue && nestedGroupValue.length > 0 ? nestedGroupValue : null;
  const relativeLabel =
    nestedGroupLabel && leafLabel
      ? `${nestedGroupLabel} › ${leafLabel}`
      : (nestedGroupLabel ?? leafLabel);
  if (mode === 'display') {
    return relativeLabel;
  }
  const suiteLabel =
    trimToNull(summary.suiteDisplayName) ?? topLevelGroup ?? trimToNull(summary.suiteName);
  if (suiteLabel && relativeLabel) {
    return `${suiteLabel} › ${relativeLabel}`;
  }
  return suiteLabel ?? relativeLabel;
};

export const mapTestRunSummaries = (
  summaries?: TestRunSummaryDto[] | null,
  nameMode: TestSummaryNameMode = 'display',
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.testSlug ?? summary.testId;
    const executionHref =
      summary.runNumber != null ? `/tests/${slug}/runs/${summary.runNumber}` : null;
    const testName = buildTestSummaryDisplayLabel(summary, nameMode);
    return {
      testId: summary.testId,
      testName,
      displayName: summary.displayName ?? null,
      groupPath: summary.groupPath ?? [],
      suiteName: summary.suiteName ?? null,
      suiteDisplayName: summary.suiteDisplayName ?? null,
      testSlug: summary.testSlug ?? null,
      href: `/tests/${slug}`,
      executionHref,
      tooltipLabel: null,
      status: normaliseSummaryChipStatus(summary.status ?? null),
      executionId: summary.executionId ?? null,
      runNumber: summary.runNumber ?? null,
      timestamp: summary.timestamp ?? null,
    };
  });
};

export const mapTestRunSummariesWithRunNumbers = (
  summaries?: TestRunSummaryDto[] | null,
  nameMode: TestSummaryNameMode = 'display',
): TestSummaryItem[] => {
  return mapTestRunSummaries(summaries, nameMode).map((summary) => ({
    ...summary,
    tooltipLabel:
      summary.runNumber !== null && summary.runNumber !== undefined
        ? `#${summary.runNumber}`
        : null,
  }));
};

export const mapSuiteRunSummaries = (
  summaries?: SuiteRunSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.suiteSlug ?? summary.suiteId;
    const executionHref =
      summary.runNumber != null ? `/suites/${slug}/runs/${summary.runNumber}` : null;
    return {
      testId: summary.suiteId,
      testName: trimToNull(summary.displayName) ?? trimToNull(summary.suiteName),
      displayName: summary.displayName ?? null,
      testSlug: summary.suiteSlug ?? null,
      href: `/suites/${slug}`,
      executionHref,
      tooltipLabel: null,
      status: normaliseSummaryChipStatus(summary.status ?? null),
      executionId: summary.executionId ?? null,
      runNumber: summary.runNumber ?? null,
      timestamp: summary.timestamp ?? null,
    };
  });
};

export const mapSuiteRunSummariesWithRunNumbers = (
  summaries?: SuiteRunSummaryDto[] | null,
): TestSummaryItem[] => {
  return mapSuiteRunSummaries(summaries).map((summary) => ({
    ...summary,
    tooltipLabel:
      summary.runNumber !== null && summary.runNumber !== undefined
        ? `#${summary.runNumber}`
        : null,
  }));
};

export const mapReleaseFeatureSummaries = (
  summaries?: ReleaseFeatureSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/features/${slug}`,
      executionHref: null,
      tooltipLabel: null,
      status: summary.status,
      executionId: null,
      runNumber: null,
    };
  });
};

export const mapReleaseSuiteSummaries = (
  summaries?: ReleaseSuiteSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/suites/${slug}`,
      executionHref: null,
      tooltipLabel: null,
      status: null,
      executionId: null,
      runNumber: null,
    };
  });
};

export const mapReleaseTestSummaries = (
  summaries?: ReleaseTestSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/tests/${slug}`,
      executionHref: null,
      tooltipLabel: null,
      status: null,
      executionId: null,
      runNumber: null,
    };
  });
};

export type SummaryTooltipItem = {
  key: string;
  detailLabel: string | null;
  executionLabel: string | null;
  href: string;
  executionHref: string | null;
  runNumber: number | null;
  status: StatusChipStatus | null;
  timestamp: Date | string | null;
};

const capitalizeLabel = (value: string): string =>
  value.length > 0 ? `${value[0].toUpperCase()}${value.slice(1)}` : value;

const resolveExecutionHref = (
  unitLabel: string,
  slug: string,
  runNumber: number | null,
): string | null => {
  if (runNumber === null) return null;
  if (unitLabel === 'suite') return `/suites/${slug}/runs/${runNumber}`;
  if (unitLabel === 'test') return `/tests/${slug}/runs/${runNumber}`;
  return null;
};

const summaryStatusMatches = (
  summaryStatus: StatusChipStatus | null | undefined,
  requestedStatus: StatusChipStatus | null | undefined,
): boolean => {
  if (requestedStatus === undefined) {
    return true;
  }

  if (requestedStatus === null) {
    return !summaryStatus;
  }

  if (requestedStatus === 'passed') {
    return summaryStatus === 'passed' || summaryStatus === 'completed';
  }

  if (requestedStatus === 'errored') {
    return summaryStatus === 'errored' || summaryStatus === 'completed_with_errors';
  }

  return summaryStatus === requestedStatus;
};

export const getSummaryTooltipItems = (
  items: TestSummaryItem[] | null | undefined,
  options: { unitLabel: string; status?: StatusChipStatus | null },
): SummaryTooltipItem[] => {
  const resolvedItems = Array.isArray(items) ? items : [];
  const filteredItems =
    options.status === undefined
      ? resolvedItems
      : resolvedItems.filter((item) => summaryStatusMatches(item.status, options.status));
  return filteredItems.map((item) => {
    const name =
      typeof item.testName === 'string' && item.testName.trim().length > 0
        ? item.testName
        : `Unknown ${capitalizeLabel(options.unitLabel)}`;
    const slug =
      typeof item.testSlug === 'string' && item.testSlug.trim().length > 0
        ? item.testSlug
        : item.testId;
    const href = item.href ?? `/${options.unitLabel}s/${slug}`;
    const executionHref =
      item.executionHref ?? resolveExecutionHref(options.unitLabel, slug, item.runNumber ?? null);
    const executionLabel =
      typeof item.tooltipLabel === 'string' && item.tooltipLabel.trim().length > 0
        ? item.tooltipLabel
        : item.runNumber !== null && item.runNumber !== undefined
          ? `#${item.runNumber}`
          : null;
    return {
      key: `${item.testId}-${item.executionId ?? item.runNumber ?? 'none'}-${options.status ?? 'all'}`,
      detailLabel: name,
      executionLabel,
      href,
      executionHref,
      runNumber: item.runNumber ?? null,
      status: item.status ?? null,
      timestamp: item.timestamp ?? null,
    };
  });
};

export const getSummaryTooltipFallbackLabel = (unitLabel: string): string =>
  `${capitalizeLabel(unitLabel)}s`;
